/**
 * Cart Drawer Recommendations – Algolia trending items carousel.
 * Fetches on cart drawer open. Resolves Arabic titles via Shopify product JSON.
 */
class CartDrawerRecommendations extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    const dialog = this.closest('dialog');
    if (dialog) this.#observeDialog(dialog);
  }

  #observeDialog(dialog) {
    const observer = new MutationObserver(() => {
      if (dialog.open && !this.#loaded) this.#run();
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    if (dialog.open) this.#run();
  }

  get #isArabic() {
    return (
      document.documentElement.lang === 'ar' ||
      window.location.pathname.startsWith('/ar/')
    );
  }

  get #localePath() {
    return this.#isArabic ? '/ar' : '';
  }

  async #run() {
    const {
      algoliaAppId: appId,
      algoliaApiKey: apiKey,
      algoliaIndex: indexName,
      limit,
    } = this.dataset;

    if (!appId || !apiKey || !indexName) return;

    const maxItems = Math.min(6, Math.max(3, parseInt(limit, 10) || 4));
    const algoliaUrl = `https://${appId}-dsn.algolia.net/1/indexes/*/recommendations`;
    const algoliaHeaders = {
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': apiKey,
      'Content-Type': 'application/json',
    };
    const baseParams = {
      distinct: true,
      filters: 'inventory_available:true',
      attributesToRetrieve: [
        'objectID', 'title', 'handle', 'price',
        'compare_at_price', 'image', 'id',
      ],
    };

    try {
      // 1. Get cart items to find related products
      const cartObjectIDs = await this.#getCartObjectIDs(appId, apiKey, indexName);

      const cache = window.__algoliaRecommendCache;
      const cacheKey = ['cart', ...cartObjectIDs.sort()];
      const cachedHits = cache?.get(cacheKey);

      let rawHits;
      if (cachedHits) {
        rawHits = cachedHits;
      } else {
        // Batch all related-products + trending-items in a single POST
        const requests = cartObjectIDs.map((oid) => ({
          indexName,
          model: 'related-products',
          objectID: oid,
          maxRecommendations: Math.ceil(maxItems * 2 / Math.max(cartObjectIDs.length, 1)),
          threshold: 0,
          queryParameters: baseParams,
        }));

        // Always include trending as fallback in the same batch
        requests.push({
          indexName,
          model: 'trending-items',
          threshold: 0,
          maxRecommendations: maxItems,
          queryParameters: baseParams,
        });

        const data = await this.#xhrPost(algoliaUrl, algoliaHeaders, JSON.stringify({ requests }));
        const results = data.results || [];

        rawHits = [];
        // Merge related hits (all results except last which is trending)
        for (let i = 0; i < results.length - 1; i++) {
          rawHits.push(...(results[i].hits || []));
        }
        // Append trending hits as fallback
        if (rawHits.length < maxItems) {
          rawHits.push(...(results[results.length - 1]?.hits || []));
        }

        cache?.set(cacheKey, rawHits);
      }

      // 3. De-duplicate and exclude products already in cart
      const cartProductIDs = await this.#getCartProductIDs();
      const seen = new Set(cartProductIDs);
      const hits = [];
      for (const hit of rawHits) {
        const pid = String(hit.id);
        if (seen.has(pid)) continue;
        seen.add(pid);
        hits.push(hit);
        if (hits.length >= maxItems) break;
      }

      if (hits.length === 0) {
        this.style.display = 'none';
        return;
      }

      // Fetch translated titles from Shopify product JSON (non-blocking)
      if (this.#isArabic) {
        this.#resolveTranslatedTitles(hits);
      }

      this.#renderCards(hits);
      this.#initCarousel();
      this.#initTouchSwipe();
      this.#loaded = true;
      this.setAttribute('data-loaded', '');
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.warn('Cart drawer recommendations error:', e.message);
      }
      this.style.display = 'none';
    }
  }

  /** Get Algolia objectIDs for products currently in the cart */
  async #getCartObjectIDs(appId, apiKey, indexName) {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      if (!cart.items || cart.items.length === 0) return [];

      const productIDs = [...new Set(cart.items.map((i) => String(i.product_id)))];

      // Resolve Algolia objectIDs from Shopify product IDs
      const searchUrl = `https://${appId}-dsn.algolia.net/1/indexes/shopify_products/query`;
      const headers = {
        'X-Algolia-Application-Id': appId,
        'X-Algolia-API-Key': apiKey,
        'Content-Type': 'application/json',
      };

      const results = await Promise.all(
        productIDs.slice(0, 3).map((pid) =>
          this.#xhrPost(searchUrl, headers, JSON.stringify({
            query: '',
            tagFilters: [`product:${pid}`],
            hitsPerPage: 1,
            distinct: true,
            attributesToRetrieve: ['objectID'],
          })).catch(() => ({ hits: [] }))
        )
      );

      return results
        .map((r) => r.hits?.[0]?.objectID)
        .filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  /** Get product IDs in cart (to exclude from recommendations) */
  async #getCartProductIDs() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      return (cart.items || []).map((i) => String(i.product_id));
    } catch (e) {
      return [];
    }
  }

  async #resolveTranslatedTitles(hits) {
    // Fetch Arabic titles in parallel from Shopify product JSON endpoint
    const fetches = hits.map((hit) =>
      fetch(`${this.#localePath}/products/${hit.handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    );

    const products = await Promise.all(fetches);

    products.forEach((product, i) => {
      if (!product || !product.title) return;
      hits[i]._translatedTitle = product.title;

      // Update the DOM card title
      const card = this.querySelector(`[data-handle="${hits[i].handle}"]`);
      if (card) {
        const titleEl = card.querySelector('.algolia-related__title');
        if (titleEl) titleEl.textContent = product.title;
      }
    });
  }

  #renderCards(hits) {
    const track = this.querySelector('[data-track]');
    if (!track) return;

    track.innerHTML = '';

    for (const hit of hits) {
      const varId = hit.objectID;
      const priceCents = Math.round((hit.price || 0) * 100);
      const compareCents = Math.round((hit.compare_at_price || 0) * 100);
      const productUrl = `${this.#localePath}/products/${hit.handle}?variant=${varId}`;
      const imgSrc = hit.image || '';
      const title = hit._translatedTitle || hit.title || '';

      const compareHtml =
        compareCents > priceCents
          ? `<s class="algolia-related__compare">${this.#formatMoney(compareCents)}</s>`
          : '';

      const card = document.createElement('div');
      card.className = 'algolia-related__card';
      card.dataset.handle = hit.handle;
      card.innerHTML = `
        <a href="${productUrl}" class="algolia-related__img-wrap">
          ${imgSrc ? `<img src="${imgSrc}" alt="${title.replace(/"/g, '&quot;')}" width="300" height="300" loading="lazy">` : ''}
        </a>
        <div class="algolia-related__info">
          <a href="${productUrl}" class="algolia-related__title-link">
            <p class="algolia-related__title">${title}</p>
          </a>
          <p class="algolia-related__price icon">
            ${compareHtml}
            <span>${this.#formatMoney(priceCents)}</span>
          </p>
        </div>
        <button type="button" class="algolia-related__btn" data-add-variant="${varId}">
          + ${this.dataset.addToCartText || 'Add'}
        </button>`;

      track.appendChild(card);
    }

    // Show the carousel
    const carousel = this.querySelector('[data-carousel]');
    if (carousel) carousel.style.display = 'block';

    // Bind add-to-cart
    track.querySelectorAll('[data-add-variant]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.#addToCart(btn);
      });
    });
  }

  #initCarousel() {
    const track = this.querySelector('[data-track]');
    const prevBtn = this.querySelector('[data-arrow-prev]');
    const nextBtn = this.querySelector('[data-arrow-next]');
    if (!track || !prevBtn || !nextBtn) return;

    const isRTL = getComputedStyle(track).direction === 'rtl';

    const updateArrows = () => {
      // In RTL, scrollLeft can be 0 → negative (Chrome/Firefox) or positive → 0 (Safari)
      // Use a reliable check: compare first/last card visibility
      const cards = track.querySelectorAll('.algolia-related__card');
      if (cards.length === 0) return;

      const trackRect = track.getBoundingClientRect();
      const firstRect = cards[0].getBoundingClientRect();
      const lastRect = cards[cards.length - 1].getBoundingClientRect();

      if (isRTL) {
        // RTL: first card is on the right, last card is on the left
        const atStart = firstRect.right <= trackRect.right + 2;
        const atEnd = lastRect.left >= trackRect.left - 2;
        prevBtn.hidden = atStart;
        nextBtn.hidden = atEnd;
      } else {
        const atStart = firstRect.left >= trackRect.left - 2;
        const atEnd = lastRect.right <= trackRect.right + 2;
        prevBtn.hidden = atStart;
        nextBtn.hidden = atEnd;
      }
    };

    const scrollAmt = () => {
      const card = track.querySelector('.algolia-related__card');
      if (!card) return track.clientWidth * 0.75;
      const gap = parseFloat(getComputedStyle(track).gap) || 10;
      return (card.offsetWidth + gap) * 2;
    };

    // In RTL, "next" means scrolling toward unseen content (to the left visually)
    // scrollBy with negative left in RTL scrolls toward the left = toward more content
    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: isRTL ? scrollAmt() : -scrollAmt(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: isRTL ? -scrollAmt() : scrollAmt(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateArrows, { passive: true });
    new ResizeObserver(updateArrows).observe(track);
    updateArrows();
  }

  #initTouchSwipe() {
    const track = this.querySelector('[data-track]');
    if (!track) return;

    let startX = 0;
    let scrollStart = 0;
    let isDragging = false;

    track.addEventListener('touchstart', (e) => {
      isDragging = true;
      startX = e.touches[0].clientX;
      scrollStart = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const dx = e.touches[0].clientX - startX;
      track.scrollLeft = scrollStart - dx;
    }, { passive: true });

    track.addEventListener('touchend', () => {
      isDragging = false;
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
    }, { passive: true });

    // Mouse drag for desktop
    track.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX;
      scrollStart = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      track.scrollLeft = scrollStart - (e.clientX - startX);
    });

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
      track.style.cursor = '';
      track.style.userSelect = '';
    };
    track.addEventListener('mouseup', endDrag);
    track.addEventListener('mouseleave', endDrag);
  }

  async #addToCart(btn) {
    const variantId = btn.dataset.addVariant;
    if (!variantId) return;

    btn.disabled = true;
    const originalHTML = btn.innerHTML;
    btn.textContent = this.dataset.addingText || 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });

      if (!res.ok) throw new Error((await res.json()).message || 'Could not add');

      btn.textContent = this.dataset.addedText || 'Added ✓';

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );

      setTimeout(() => {
        btn.innerHTML = originalHTML;
        btn.disabled = false;
      }, 2000);
    } catch (e) {
      console.error('Add to cart error:', e.message);
      btn.innerHTML = originalHTML;
      btn.disabled = false;
    }
  }

  /** XHR POST that bypasses patched window.fetch (search-cache, etc.) */
  #xhrPost(url, headers, body) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      for (const [k, v] of Object.entries(headers)) {
        xhr.setRequestHeader(k, v);
      }
      xhr.onload = () => {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { reject(new Error('JSON parse error')); }
      };
      xhr.onerror = () => reject(new Error('XHR network error'));
      xhr.send(body);
    });
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const moneyFormat =
      this.dataset.moneyFormat ||
      window.Shopify?.currency?.moneyFormat ||
      '{{amount}} SAR';
    return moneyFormat
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString())
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount}}', amount);
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
